import { useEffect, useState } from "react";
import { estimateWords } from "./AutomationsTabModel";
import { useAutomationsApi, useReference } from "./hooks";
import type { AutomationDraft, Step, StepAction, StepSample } from "./types";
import { APPROVER_ONE } from "./words";

/**
 * The actions whose line the backend words: whose turn it is, or what a
 * note's variables fill in as, can only come from it.
 */
const FROM_BACKEND: StepAction[] = ["close_task", "assign_tutor", "add_note"];

/** How long the draft stays the same before the backend is asked. */
const SETTLE_MS = 500;

/** "If it ran for Lily Chen now, it would assign Sam Doe to Lily Chen." */
function sampleWords(sample: StepSample, step: Step) {
  const lead = sample.person
    ? `If it ran for ${sample.person} now`
    : "If it ran now";
  if (sample.skip) return `${lead}, it would be skipped: ${sample.skip}`;
  if (!sample.would) return null;
  // A note's own last period ("…: “Sent the welcome packet.”") ends it.
  const would = /[.!?]”?$/.test(sample.would)
    ? sample.would
    : `${sample.would}.`;
  if (!step.askFirst.on) return `${lead}, it would ${would}`;
  return `${lead}, it would wait for ${
    APPROVER_ONE[step.askFirst.approvers ?? "admins"]
  } to approve, then ${would}`;
}

/**
 * The step dialog's "If it ran for … now, it would …" line. For Close a
 * task, Assign a tutor and Add a note, the backend's preview words it once
 * the draft settles, for a step timed from the event; until then, and for
 * any other step, the line is worked out here (estimateWords).
 */
export default function useStepEstimate(
  automationId: string | null,
  draft: AutomationDraft | null,
  step: Step | null
): string | null {
  const ref = useReference();
  const api = useAutomationsApi();
  const local = draft && step ? estimateWords(ref, draft, step) : null;
  const key =
    draft && step && FROM_BACKEND.includes(step.action)
      ? JSON.stringify([automationId, step.id, draft.trigger, draft.steps])
      : null;
  const [asked, setAsked] = useState<{
    key: string;
    words: string | null;
  } | null>(null);

  useEffect(() => {
    if (!key || !draft || !step) return;
    let current = true;
    const timer = setTimeout(() => {
      api
        .sampleStep({ id: automationId, draft }, step.id)
        .then(
          (sample) =>
            current &&
            setAsked({ key, words: sample ? sampleWords(sample, step) : null })
        )
        // A draft the preview refuses (a step not finished yet) keeps the
        // line worked out here; Save says what is missing.
        .catch(() => current && setAsked({ key, words: null }));
    }, SETTLE_MS);
    return () => {
      current = false;
      clearTimeout(timer);
    };
    // The key holds everything the backend is asked about.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return (asked && asked.key === key && asked.words) || local;
}
